import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  listArticlesAdmin, createArticle, updateArticle, deleteArticle,
  uploadArticleImage, getCourses,
} from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Badge } from '../../components/ui/badge';
import { Plus, Edit, Trash2, Image as ImageIcon, Newspaper, ExternalLink, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import ArticleRichEditor from '../../components/ArticleRichEditor';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const slugify = (s) => (s || '').toLowerCase()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9\s-]/g, '')
  .trim().replace(/\s+/g, '-').slice(0, 80);

const emptyForm = () => ({
  title: '', slug: '', body: '', excerpt: '',
  featured_image: '', course_id: '_none', status: 'draft',
  seo_title: '', seo_description: '',
});

const HelpIcon = ({ title, children }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" className="inline-flex items-center justify-center w-5 h-5 rounded-full text-muted-foreground hover:text-primary hover:bg-secondary transition-colors" aria-label="Ayuda">
        <HelpCircle className="w-4 h-4" />
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-80 text-sm" side="right" align="start">
      <p className="font-semibold text-primary mb-2">{title}</p>
      <div className="text-xs text-muted-foreground space-y-1.5 leading-relaxed">{children}</div>
    </PopoverContent>
  </Popover>
);

export const ManageArticles = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const coverRef = useRef();

  useEffect(() => {
    if (user && !['superadmin', 'admin', 'professor'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [a, c] = await Promise.all([listArticlesAdmin(), getCourses()]);
      setArticles(a.data || []);
      setCourses(c.data || []);
    } catch {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const resetForm = () => {
    setForm(emptyForm());
    setEditing(null);
    setCoverFile(null);
    setCoverPreview(null);
  };

  const handleEdit = (a) => {
    setEditing(a);
    setForm({
      title: a.title, slug: a.slug, body: a.body || '', excerpt: a.excerpt || '',
      featured_image: a.featured_image || '',
      course_id: a.course_id || '_none',
      status: a.status, seo_title: a.seo_title || '', seo_description: a.seo_description || '',
    });
    setCoverFile(null);
    setCoverPreview(a.featured_image
      ? (a.featured_image.startsWith('/api') ? `${API_BASE}${a.featured_image}` : a.featured_image)
      : null);
    setDialogOpen(true);
  };

  const handleCoverPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCoverFile(f);
    setCoverPreview(URL.createObjectURL(f));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.slug || !form.body) {
      toast.error('Título, slug y contenido son obligatorios');
      return;
    }
    setUploading(true);
    try {
      // Upload cover first if a new file selected
      let featured = form.featured_image;
      if (coverFile) {
        const fd = new FormData();
        fd.append('file', coverFile);
        const r = await uploadArticleImage(fd);
        featured = r.data.url;
      }
      const payload = {
        title: form.title,
        slug: form.slug,
        body: form.body,
        excerpt: form.excerpt || null,
        featured_image: featured || null,
        course_id: form.course_id && form.course_id !== '_none' ? form.course_id : null,
        status: form.status,
        seo_title: form.seo_title || null,
        seo_description: form.seo_description || null,
      };
      if (editing) {
        await updateArticle(editing.id, payload);
        toast.success('Artículo actualizado');
      } else {
        await createArticle(payload);
        toast.success('Artículo creado');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este artículo?')) return;
    try {
      await deleteArticle(id);
      toast.success('Artículo eliminado');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  if (loading) return <div className="p-8">Cargando...</div>;

  return (
    <div className="p-6 md:p-12 bg-secondary min-h-screen" data-testid="manage-articles-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold mb-2">
              <Newspaper className="w-3.5 h-3.5" /> CMS
            </div>
            <h1 className="text-3xl font-heading font-bold text-primary mb-2">Gestionar Artículos</h1>
            <p className="text-muted-foreground">Crea, edita y publica artículos vinculados a tus cursos.</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-article-btn"><Plus className="w-4 h-4 mr-2" />Nuevo Artículo</Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editing ? 'Editar Artículo' : 'Nuevo Artículo'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Título *</Label>
                    <Input value={form.title}
                      onChange={e => {
                        const t = e.target.value;
                        setField('title', t);
                        if (!editing) setField('slug', slugify(t));
                      }}
                      required data-testid="article-title-input" placeholder="Título del artículo" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label>Slug (URL) *</Label>
                      <HelpIcon title="¿Qué es el slug?">
                        <p>Es la dirección amigable del artículo. Ejemplo: <em>iaasp-anuncia-nuevo-curso</em>.</p>
                        <p>Solo letras minúsculas, números y guiones. Sin espacios ni acentos.</p>
                        <p>Se genera automáticamente desde el título, pero puedes editarlo.</p>
                      </HelpIcon>
                    </div>
                    <Input value={form.slug} onChange={e => setField('slug', slugify(e.target.value))}
                      required data-testid="article-slug-input" placeholder="mi-articulo" />
                  </div>
                </div>

                <div>
                  <Label>Resumen / Excerpt</Label>
                  <Textarea value={form.excerpt} onChange={e => setField('excerpt', e.target.value)}
                    rows={2} placeholder="Una línea o dos resumiendo el artículo (aparece en el listado y en redes sociales)." />
                </div>

                {/* Featured image */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Imagen destacada</Label>
                    <HelpIcon title="Imagen destacada">
                      <p><strong>Tamaño recomendado:</strong> <span className="text-primary font-semibold">1280 × 720 px</span> (16:9).</p>
                      <p><strong>Formatos:</strong> JPG, PNG, WEBP, GIF.</p>
                      <p>Se redimensiona automáticamente a un máximo de 1920 px y se comprime para optimizar la velocidad.</p>
                    </HelpIcon>
                  </div>
                  <div className="flex gap-3 items-start mt-1">
                    <div onClick={() => coverRef.current?.click()}
                      className="flex-1 border-2 border-dashed rounded-xl p-4 text-center cursor-pointer hover:border-primary transition-colors min-h-[120px] flex items-center justify-center">
                      {coverPreview ? (
                        <img src={coverPreview} alt="cover" className="h-32 mx-auto object-cover rounded-lg" />
                      ) : (
                        <div>
                          <ImageIcon className="w-8 h-8 mx-auto text-muted-foreground mb-1" />
                          <p className="text-xs text-muted-foreground">Clic para subir imagen</p>
                          <p className="text-[11px] text-muted-foreground/70 mt-0.5">1280 × 720 px · auto-optimizada</p>
                        </div>
                      )}
                      <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={handleCoverPick} />
                    </div>
                  </div>
                </div>

                {/* Course relation */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Curso vinculado (opcional)</Label>
                    <Select value={form.course_id} onValueChange={v => setField('course_id', v)}>
                      <SelectTrigger data-testid="article-course-select"><SelectValue placeholder="Sin curso" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">Sin curso vinculado</SelectItem>
                        {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Estado</Label>
                    <Select value={form.status} onValueChange={v => setField('status', v)}>
                      <SelectTrigger data-testid="article-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Borrador</SelectItem>
                        <SelectItem value="published">Publicado</SelectItem>
                        <SelectItem value="archived">Archivado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Body editor */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Contenido del artículo *</Label>
                    <HelpIcon title="Editor WYSIWYG">
                      <p>Escribe tu artículo como en Word.</p>
                      <p>Usa los botones de la barra para <strong>negrita</strong>, <em>cursiva</em>, títulos, listas, citas, enlaces e imágenes.</p>
                      <p>Las imágenes se suben optimizadas (máx 1920 px, JPEG calidad 85).</p>
                    </HelpIcon>
                  </div>
                  <ArticleRichEditor
                    value={form.body}
                    onChange={v => setField('body', v)}
                    placeholder="Comienza a escribir tu artículo..."
                  />
                </div>

                {/* SEO */}
                <details className="border rounded-lg p-3">
                  <summary className="cursor-pointer text-sm font-semibold text-muted-foreground">SEO (opcional)</summary>
                  <div className="mt-3 space-y-3">
                    <div>
                      <Label className="text-xs">Título SEO</Label>
                      <Input value={form.seo_title} onChange={e => setField('seo_title', e.target.value)}
                        placeholder="Si no se llena, se usa el título del artículo." />
                    </div>
                    <div>
                      <Label className="text-xs">Meta descripción</Label>
                      <Textarea value={form.seo_description} onChange={e => setField('seo_description', e.target.value)}
                        rows={2} placeholder="Si no se llena, se usa el excerpt." />
                    </div>
                  </div>
                </details>

                <div className="flex gap-2 pt-4 border-t">
                  <Button type="submit" disabled={uploading} data-testid="save-article-btn">
                    {uploading ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear')} Artículo
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* List */}
        {articles.length === 0 ? (
          <Card><CardContent className="py-16 text-center text-muted-foreground">
            <Newspaper className="w-10 h-10 mx-auto mb-3 opacity-40" />
            Aún no has creado ningún artículo. ¡Crea el primero!
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {articles.map(a => (
              <Card key={a.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex gap-4">
                    {a.featured_image && (
                      <img
                        src={a.featured_image.startsWith('/api') ? `${API_BASE}${a.featured_image}` : a.featured_image}
                        alt={a.title}
                        className="w-28 h-20 object-cover rounded-lg shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="text-lg font-semibold truncate">{a.title}</h3>
                        <Badge variant={a.status === 'published' ? 'default' : 'secondary'}>{a.status}</Badge>
                        {a.course_title && (
                          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Curso: {a.course_title}</Badge>
                        )}
                      </div>
                      {a.excerpt && <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{a.excerpt}</p>}
                      <div className="text-xs text-muted-foreground">
                        {a.author_name && <span>Por {a.author_name} · </span>}
                        {new Date(a.created_at).toLocaleDateString('es-ES')}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {a.status === 'published' && (
                        <Link to={`/articulos/${a.slug}`} target="_blank">
                          <Button size="sm" variant="outline" title="Ver publicado">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </Link>
                      )}
                      <Button size="sm" variant="outline" onClick={() => handleEdit(a)} data-testid={`edit-article-${a.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(a.id)} data-testid={`delete-article-${a.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageArticles;
